/**
 * Photos de la copie et de l'énoncé d'une éval (migration 0333) : l'élève les
 * dépose (bucket privé `evaluation-photos`), ses profs liés les lisent.
 * Dépôt = Storage PUIS ligne, avec retrait de l'objet si la ligne échoue ;
 * suppression = Storage puis ligne (calque `@jeraisonne/documents`).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

const db = supabase as unknown as SupabaseClient;

export const EVALUATION_PHOTOS_BUCKET = 'evaluation-photos';
export const EVALUATION_PHOTO_MAX_BYTES = 10 * 1024 * 1024;
export const EVALUATION_PHOTOS_MAX = 10;
export const EVALUATION_PHOTO_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'application/pdf',
] as const;

export type EvaluationPhotoKind = 'copie' | 'enonce';

export interface EvaluationPhoto {
  id: string;
  evaluation_id: string;
  student_id: string;
  kind: EvaluationPhotoKind;
  storage_path: string;
  mime_type: string;
  size_bytes: number;
  created_at: string;
}

export const EVALUATION_PHOTO_KIND_LABEL: Record<EvaluationPhotoKind, string> = {
  copie: 'Copie',
  enonce: 'Énoncé',
};

/** Raison du refus d'un fichier, sinon null. */
export function evaluationFileIssue(file: { type: string; size: number }): string | null {
  if (!(EVALUATION_PHOTO_TYPES as readonly string[]).includes(file.type)) {
    return 'Formats acceptés : photo (JPEG, PNG, WebP, HEIC) ou PDF.';
  }
  if (file.size <= 0) return 'Fichier vide.';
  if (file.size > EVALUATION_PHOTO_MAX_BYTES) return 'Fichier trop lourd (10 Mo maximum).';
  return null;
}

/** Chemin Storage `{élève}/{éval}/{id}.{ext}` (verrouillé par un CHECK en base). */
export function evaluationPhotoPath(
  studentId: string,
  evaluationId: string,
  photoId: string,
  mimeType: string,
): string {
  const ext =
    mimeType === 'application/pdf'
      ? 'pdf'
      : (mimeType.split('/')[1] ?? 'jpg').replace('jpeg', 'jpg');
  return `${studentId}/${evaluationId}/${photoId}.${ext}`;
}

export function evaluationPhotoErrorMessage(message: string): string {
  if (message.includes('evaluation_photos_limit')) {
    return `${EVALUATION_PHOTOS_MAX} photos au maximum par éval.`;
  }
  return 'L’envoi n’a pas abouti. Réessaie.';
}

export const evaluationPhotosKey = (scope: string) => ['evaluation-photos', scope] as const;

/** Photos d'un élève (toutes ses évals) — l'élève lui-même ou son prof lié. */
export function useStudentEvaluationPhotos(studentId: string | null | undefined) {
  return useQuery({
    queryKey: evaluationPhotosKey(studentId ?? ''),
    enabled: !!studentId,
    staleTime: 30_000,
    queryFn: async (): Promise<EvaluationPhoto[]> => {
      const { data, error } = await db
        .from('student_evaluation_photos')
        .select(
          'id, evaluation_id, student_id, kind, storage_path, mime_type, size_bytes, created_at',
        )
        .eq('student_id', studentId)
        .order('created_at', { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as EvaluationPhoto[];
    },
  });
}

/** URLs signées (1 h) des photos, par chemin. */
export function usePhotoUrls(paths: string[]) {
  return useQuery({
    queryKey: ['evaluation-photo-urls', ...paths],
    enabled: paths.length > 0,
    staleTime: 30 * 60_000,
    queryFn: async (): Promise<Record<string, string>> => {
      const { data, error } = await db.storage
        .from(EVALUATION_PHOTOS_BUCKET)
        .createSignedUrls(paths, 3600);
      if (error) throw new Error(error.message);
      const out: Record<string, string> = {};
      for (const row of data ?? []) {
        if (row.path && row.signedUrl) out[row.path] = row.signedUrl;
      }
      return out;
    },
  });
}

export function useUploadEvaluationPhotos(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      evaluationId: string;
      kind: EvaluationPhotoKind;
      files: File[];
    }): Promise<number> => {
      let done = 0;
      for (const file of vars.files) {
        const issue = evaluationFileIssue(file);
        if (issue) throw new Error(issue);
        const id = crypto.randomUUID();
        const path = evaluationPhotoPath(studentId, vars.evaluationId, id, file.type);
        const up = await db.storage
          .from(EVALUATION_PHOTOS_BUCKET)
          .upload(path, file, { contentType: file.type, upsert: false });
        if (up.error) throw new Error(up.error.message);
        const { error } = await db.from('student_evaluation_photos').insert({
          id,
          evaluation_id: vars.evaluationId,
          student_id: studentId,
          kind: vars.kind,
          storage_path: path,
          mime_type: file.type,
          size_bytes: file.size,
        });
        if (error) {
          await db.storage.from(EVALUATION_PHOTOS_BUCKET).remove([path]);
          throw new Error(error.message);
        }
        done++;
      }
      return done;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: evaluationPhotosKey(studentId) }),
  });
}

export function useDeleteEvaluationPhoto(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (photo: EvaluationPhoto) => {
      const rm = await db.storage.from(EVALUATION_PHOTOS_BUCKET).remove([photo.storage_path]);
      if (rm.error) throw new Error(rm.error.message);
      const { error } = await db.from('student_evaluation_photos').delete().eq('id', photo.id);
      if (error) throw new Error(error.message);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: evaluationPhotosKey(studentId) }),
  });
}
